// components/SectionContainer.tsx
import React, { useState } from 'react';
import styled from 'styled-components';
import InnerElement from './InnerElement';
import { Section } from '../../types/types';
import { useTheme } from '../../middleware/ThemeContext';
import { Theme } from '../../utils/themes';
import { hexToRgba } from '../../utils/helper';
import DeleteIcon from '../../assets/icons/DeleteIcon';
import CopyIcon from '../../assets/icons/CopyIcon';
import NewCopyIcon from '../../assets/icons/NewCopyIcon';
import { StyledVr } from '../../features/organizations/OrganizationPreview';
import StyledText from '../StyledComponents/StyledText';
import DragIcon from '../../assets/icons/DragIcon';

const SectionContainerWrapper = styled.div<{
    isSelected?: boolean;
    isDragOver?: boolean;
    theme: Theme;
    $bgColor?: string;
    $backgroundImage?: string;
}>`
        background-color: ${({ $bgColor, isSelected, theme }) =>
        $bgColor || (isSelected ? hexToRgba(theme.CorporateTheme.colors.blueberry, 0.05) : hexToRgba(theme.CorporateTheme.colors.blueberry, 0.05))};
        background-image: ${({ $backgroundImage }) => $backgroundImage || 'none'};
        background-size: cover;
        background-position: center;
        background-repeat: no-repeat;
        border: 1px dashed ${({ isDragOver, theme }) => isDragOver ? theme.CorporateTheme.colors.blueberry : 'transparent'};
  border-radius: 4px;
  padding: 45px 45px;
//   margin-bottom: 15px;
  min-height: 150px;
  position: relative;
  transition: all 0.3s;
    cursor: grab;
    user-select: none;
    -webkit-user-select: none;
    -webkit-user-drag: element;
  display: flex;
  flex-direction: column;
  justify-content: center;
  
  &:hover {
  border: 1px dashed ${({ isDragOver, theme }) => isDragOver ? theme.CorporateTheme.colors.blueberry : theme.CorporateTheme.colors.count};
    // border-color: #6c63ff;
  }
  
  &.hoverable {
    /* Parent class for toolbar hover */
  }
`;

const SectionLabel = styled.div`
  position: absolute;
  top: -10px;
  left: 15px;
  background: #6c63ff;
  color: white;
  padding: 4px 12px;
  border-radius: 4px;
  font-size: 11px;
  font-weight: 600;
  text-transform: uppercase;
  letter-spacing: 0.5px;
  z-index: 1;
`;

const ElementToolbar = styled.div<{ isVisible?: boolean }>`
  position: absolute;
  top: -33px;
  right: 0px;
  display: flex;
  z-index: 99;
    opacity: ${props => props.isVisible ? 1 : 0};
  transition: opacity 0.2s;
    pointer-events: ${props => props.isVisible ? 'all' : 'none'};
`;

const ToolbarButton = styled.button`
  width: 28px;
  height: 28px;
  border: none;
  background: white;
  border-radius: 4px;
  cursor: pointer;
  display: flex;
  align-items: center;
  justify-content: center;
  font-size: 14px;
  transition: all 0.2s;
  box-shadow: 0 1px 3px rgba(0,0,0,0.1);
  
  &:hover {
    background: #f8f9fa;
    transform: scale(1.1);
  }
`;

const SingleColumnContent = styled.div`
//   padding-top: 15px;
  display: flex;
  flex-direction: column;
  justify-content: center;
  gap: 15px;
`;

const InnerDropZone = styled.div<{ isOver?: boolean, theme: Theme }>`
    background: ${props => props.isOver ? hexToRgba(props.theme.CorporateTheme.colors.blueberry, 0.15) : props.theme.CorporateTheme.colors.background};
    // border: ${props => props.isOver ? `1px dashed ${props.theme.CorporateTheme.colors.blueberry}` : `1px dashed ${props.theme.CreateFormTheme.colors.inputBorders}`};
  padding: 30px 15px;
  text-align: center;
  color: #adb5bd;
  font-size: 13px;
  transition: all 0.3s;
  min-height: 62px;
  display: flex;
  align-items: center;
  justify-content: center;
`;

const ElementInsertZone = styled.div<{ theme: Theme; $isActive: boolean; $isVisible: boolean }>`
    display: block;
    opacity: ${({ $isVisible }) => ($isVisible ? 1 : 0)};
    pointer-events: ${({ $isVisible }) => ($isVisible ? 'auto' : 'none')};
    height: ${({ $isVisible, $isActive }) => {
        if (!$isVisible) return '0px';
        return $isActive ? '56px' : '50px';
    }};
    padding: ${({ $isVisible }) => ($isVisible ? '0' : '0')};
    margin: 0;
    overflow: hidden;
    border-radius: 6px;
    border: ${({ $isVisible, $isActive, theme }) => {
        if (!$isVisible) return '1px dashed transparent';
        if ($isActive) return `1px solid ${theme.CorporateTheme.colors.blueberry}`;
        return `1px dashed ${theme.CreateFormTheme.colors.inputBorders}`;
    }};
    background: ${({ $isVisible, $isActive, theme }) => {
        if (!$isVisible) return 'transparent';
        if ($isActive) return hexToRgba(theme.CorporateTheme.colors.blueberry, 0.08);
        return theme.CorporateTheme.colors.filterBG;
    }};
    transition: all 0.2s ease;
`;

const MultiColumnGrid = styled.div`
  display: grid;
  grid-template-columns: repeat(2, 1fr);
    gap: 20px;
`;

const ColumnContainer = styled.div<{ isOver?: boolean, theme: Theme }>`
    background: ${props => props.isOver ? hexToRgba(props.theme.CorporateTheme.colors.blueberry, 0.07) : 'transparent'};
    // border: 1px dashed ${props => props.isOver ? props.theme.CorporateTheme.colors.blueberry : 'transparent'};
  border-radius: 6px;
//   min-height: 150px;
  transition: all 0.3s;
  display: flex;
  flex-direction: column;
  gap: 15px;
  
//   &:hover {
//     border-color: #6c63ff;
//     background: #f8f9ff;
//   }
`;

const ColumnPlaceholder = styled.div<{ theme: Theme; isOver?: boolean }>`
  text-align: center;
  padding: 30px 15px;
  color: #adb5bd;
  font-size: 13px;
//   flex: 1;
  display: flex;
  align-items: center;
  justify-content: center;
    background: ${props => props.isOver
        ? hexToRgba(props.theme.CorporateTheme.colors.blueberry, 0.07)
        : props.theme.CorporateTheme.colors.background};
    transition: all 0.3s;
`;

interface SectionContainerProps {
    section: Section;
    selectedElement: string | null;
    setSelectedElement: (id: string | null) => void;
    isOverSection: string | null;
    setIsOverSection: (id: string | null) => void;
    isOverColumn: { sectionId: string; column: 'left' | 'right' } | null;
    setIsOverColumn: (config: { sectionId: string; column: 'left' | 'right' } | null) => void;
    draggedElement: string | null;
    draggedCanvasItem?:
    | { kind: 'section'; sectionId: string }
    | { kind: 'element'; sectionId: string; column: 'single' | 'left' | 'right'; elementId: string }
    | null;
    onSectionDrop: (e: React.DragEvent<HTMLDivElement>, sectionId: string) => void;
    onColumnDrop: (e: React.DragEvent<HTMLDivElement>, sectionId: string, column: 'left' | 'right') => void;
    onSectionDragStart: (sectionId: string) => void;
    onSectionDragEnd: () => void;
    onElementDragStart: (sectionId: string, column: 'single' | 'left' | 'right', elementId: string) => void;
    onElementDragEnd: () => void;
    onElementDropAtIndex: (
        e: React.DragEvent<HTMLDivElement>,
        sectionId: string,
        column: 'single' | 'left' | 'right',
        index: number
    ) => void;
    activeElementDropZone: {
        sectionId: string;
        column: 'single' | 'left' | 'right';
        index: number;
    } | null;
    setActiveElementDropZone: (zone: {
        sectionId: string;
        column: 'single' | 'left' | 'right';
        index: number;
    } | null) => void;
    onDeleteTopLevel: (id: string) => void;
    onDuplicateSection: (id: string) => void;
    onDeleteFromSection: (sectionId: string, elementId: string) => void;
    onDuplicateFromSection: (sectionId: string, elementId: string) => void;
    onDeleteFromColumn: (sectionId: string, column: 'left' | 'right', elementId: string) => void;
    onDuplicateFromColumn: (sectionId: string, column: 'left' | 'right', elementId: string) => void;
    onOpenSettings: (config: any) => void;
    pendingChanges?: Map<string, any>;
    primaryFont?: string;
}

const SectionContainer: React.FC<SectionContainerProps> = ({
    section,
    selectedElement,
    setSelectedElement,
    isOverSection,
    setIsOverSection,
    isOverColumn,
    setIsOverColumn,
    draggedElement,
    draggedCanvasItem = null,
    onSectionDrop,
    onColumnDrop,
    onSectionDragStart,
    onSectionDragEnd,
    onElementDragStart,
    onElementDragEnd,
    onElementDropAtIndex,
    activeElementDropZone,
    setActiveElementDropZone,
    onDeleteTopLevel,
    onDuplicateSection,
    onDeleteFromSection,
    onDuplicateFromSection,
    onDeleteFromColumn,
    onDuplicateFromColumn,
    onOpenSettings,
    pendingChanges = new Map(),
    primaryFont
}) => {
    const { theme } = useTheme();
    const hasLeftContent = section.columns && section.columns.left.length > 0;
    const hasRightContent = section.columns && section.columns.right.length > 0;
    const hasSingleContent = section.type === 'single_column' && section.elements.length > 0;
    const [isSectionHovered, setIsSectionHovered] = useState(false);
    const [isElementHovered, setIsElementHovered] = useState(false);
    const sectionTypes = ['single_column', 'multi_column'];
    const isDraggingNewElement = Boolean(draggedElement && !sectionTypes.includes(draggedElement));
    const isDraggingExistingElement = draggedCanvasItem?.kind === 'element';
    const isDraggingElement = isDraggingNewElement || isDraggingExistingElement;

    const hasPendingChanges = pendingChanges.has(section.id);
    const appliedSettings = hasPendingChanges ? pendingChanges.get(section.id) : section.properties || {};
    const mediaType = appliedSettings?.mediaType || 'Image';
    const mediaTypePrefix = mediaType.toLowerCase();
    const backgroundImageUrl = appliedSettings?.signedUrl ||
        appliedSettings?.[`${mediaTypePrefix}Preview`] ||
        appliedSettings?.[`${mediaTypePrefix}ExternalURL`] ||
        appliedSettings?.[`${mediaTypePrefix}Url`] ||
        appliedSettings?.[`${mediaTypePrefix}URL`];

    const normalizeOpacity = (value: unknown): number | undefined => {
        if (value === undefined || value === null) return undefined;
        if (typeof value === 'number' && !Number.isNaN(value)) {
            const normalized = value > 1 ? value / 100 : value;
            return Math.min(1, Math.max(0, normalized));
        }
        if (typeof value === 'string') {
            const trimmed = value.trim();
            if (!trimmed) return undefined;
            const percentMatch = trimmed.endsWith('%');
            const parsed = parseFloat(trimmed.replace('%', ''));
            if (Number.isNaN(parsed)) return undefined;
            const normalized = percentMatch || parsed > 1 ? parsed / 100 : parsed;
            return Math.min(1, Math.max(0, normalized));
        }
        return undefined;
    };

    const overlayOpacity = normalizeOpacity(appliedSettings?.imageOverlayOpacity);
    const overlayColor = appliedSettings?.imageOverlayColor;
    const overlayRgba = overlayColor ? hexToRgba(overlayColor, overlayOpacity ?? 1) : undefined;
    const backgroundImage = backgroundImageUrl
        ? `${overlayRgba ? `linear-gradient(${overlayRgba}, ${overlayRgba}), ` : ''}url(${backgroundImageUrl})`
        : undefined;

    const isPointerInsideCurrentTarget = (e: React.DragEvent<HTMLElement>) => {
        const rect = e.currentTarget.getBoundingClientRect();
        return e.clientX >= rect.left && e.clientX <= rect.right && e.clientY >= rect.top && e.clientY <= rect.bottom;
    };

    const renderElementInsertZone = (
        column: 'single' | 'left' | 'right',
        index: number,
        style?: React.CSSProperties
    ) => {
        const isActive =
            activeElementDropZone?.sectionId === section.id &&
            activeElementDropZone?.column === column &&
            activeElementDropZone?.index === index;

        return (
            <ElementInsertZone
                theme={theme}
                $isVisible={isDraggingElement}
                $isActive={isDraggingElement && isActive}
                style={style}
                onDragOver={(e) => {
                    e.preventDefault();
                    e.stopPropagation();

                    if (!isDraggingElement) return;

                    if (!isPointerInsideCurrentTarget(e)) {
                        setActiveElementDropZone(null);
                        return;
                    }

                    setIsOverSection(null);
                    setIsOverColumn(null);
                    setActiveElementDropZone({ sectionId: section.id, column, index });
                }}
                onDragLeave={(e) => {
                    e.preventDefault();
                    e.stopPropagation();
                    setActiveElementDropZone(null);
                }}
                onDrop={(e) => onElementDropAtIndex(e, section.id, column, index)}
            />
        );
    };


    return (
        <SectionContainerWrapper
            className="hoverable"
            isSelected={selectedElement === section.id}
            isDragOver={isOverSection === section.id || isOverColumn?.sectionId === section.id}
            draggable
            onClick={() => setSelectedElement(section.id)}
            theme={theme}
            $bgColor={appliedSettings?.bgColor}
            $backgroundImage={backgroundImage}
            data-element-id={section.id}
            onDragStartCapture={(e) => {
                e.dataTransfer.effectAllowed = 'move';
                e.dataTransfer.setData('text/plain', `section:${section.id}`);
            }}
            onDragStart={(e) => {
                e.stopPropagation();
                onSectionDragStart(section.id);
            }}
            onDragEndCapture={(e) => {
                e.stopPropagation();
                onSectionDragEnd();
            }}
            onDragEnd={onSectionDragEnd}
            onMouseEnter={() => setIsSectionHovered(true)}
            onMouseLeave={() => {
                setIsSectionHovered(false);
                setIsElementHovered(false);
            }}
            onDragOver={(e) => {
                e.preventDefault();
                e.stopPropagation();

                if (!isPointerInsideCurrentTarget(e)) {
                    setIsOverSection(null);
                    return;
                }
            }}
            onDragLeave={(e) => {
                e.stopPropagation();
                setIsOverSection(null);
            }}
            onDrop={() => {
                setIsOverSection(null);
                setIsOverColumn(null);
            }}
        >
            {/* <SectionLabel>
                {section.type === 'single-column' ? 'Single Column' : 'Multi Column'}
            </SectionLabel> */}
            <ElementToolbar isVisible={isSectionHovered && !isElementHovered}>
                <div style={{ background: theme.CorporateTheme.colors.headerColor, borderRadius: '4px 0px 0 0', display: 'flex', gap: '8px', padding: '6px 12px', alignItems: 'center', justifyContent: 'center' }}>
                    <div
                        draggable
                        onDragStart={(e) => {
                            e.stopPropagation();
                            e.dataTransfer.effectAllowed = 'move';
                            e.dataTransfer.setData('text/plain', `section:${section.id}`);

                            const sectionNode = (e.currentTarget as HTMLElement).closest('[data-element-id]') as HTMLElement | null;
                            if (sectionNode) {
                                const rect = sectionNode.getBoundingClientRect();
                                e.dataTransfer.setDragImage(sectionNode, e.clientX - rect.left, e.clientY - rect.top);
                            }

                            onSectionDragStart(section.id);
                        }}
                        onDragEnd={(e) => {
                            e.stopPropagation();
                            onSectionDragEnd();
                        }}
                        className='d-flex align-items-center justify-content-center'
                        style={{ cursor: 'grab' }}
                    >
                        <DragIcon color={theme.CorporateTheme.colors.background} size={20} />
                    </div>
                    <div style={{ height: 13, width: 1, background: hexToRgba(theme.CorporateTheme.colors.white, 0.16) }} />
                    <div
                        onClick={(e) => {
                            e.stopPropagation();
                            onDeleteTopLevel(section.id);
                        }}
                        className='d-flex align-items-center justify-content-center'
                    >
                        <DeleteIcon color={theme.CorporateTheme.colors.background} size={22} />
                    </div>
                    <div style={{ height: 13, width: 1, background: hexToRgba(theme.CorporateTheme.colors.white, 0.16) }} />
                    <div
                        className='d-flex align-items-center justify-content-center'
                        onClick={(e) => {
                            e.stopPropagation();
                            onDuplicateSection(section.id);
                        }}
                    >
                        <NewCopyIcon color={theme.CorporateTheme.colors.background} size={20} />
                    </div>
                </div>

                <div
                    style={{ background: theme.CorporateTheme.colors.count, padding: '6px 12px', display: 'flex', alignItems: 'center', justifyContent: 'center', borderRadius: '0 4px 0px 0px' }}
                    onClick={(e) => {
                        e.stopPropagation();
                        onOpenSettings({
                            type: 'section',
                            itemId: section.id,
                            itemType: section.type
                        });
                    }}
                >
                    <StyledText fontSize='14px' fontWeight={600} textColor={theme.CreateFormTheme.colors.background}>Edit Section</StyledText>
                </div>
            </ElementToolbar>

            {section.type === 'single_column' ? (
                <SingleColumnContent>
                    {!isDraggingElement && !hasSingleContent ? (
                        <ColumnPlaceholder
                            theme={theme}
                            isOver={false}
                            style={{ height: '62px', padding: 0 }}
                        />
                    ) : (
                        <>
                            {section.elements.map((element, index) => (
                                <React.Fragment key={element.id}>
                                    {renderElementInsertZone('single', index)}
                                    <InnerElement
                                        element={element}
                                        selectedElement={selectedElement}
                                        setSelectedElement={setSelectedElement}
                                        onDelete={() => onDeleteFromSection(section.id, element.id)}
                                        onDuplicate={() => onDuplicateFromSection(section.id, element.id)}
                                        onOpenSettings={(config) => onOpenSettings({
                                            ...config,
                                            sectionId: section.id,
                                            column: 'single'
                                        })}
                                        onHoverChange={setIsElementHovered}
                                        pendingChanges={pendingChanges}
                                        sectionId={section.id}
                                        column="single"
                                        primaryFont={primaryFont}
                                        onDragStart={() => onElementDragStart(section.id, 'single', element.id)}
                                        onDragEnd={onElementDragEnd}
                                    />
                                </React.Fragment>
                            ))}

                            {renderElementInsertZone('single', section.elements.length)}
                        </>
                    )}
                </SingleColumnContent>
            ) : (
                <MultiColumnGrid>
                    <ColumnContainer
                        theme={theme}
                        isOver={isOverColumn?.sectionId === section.id && isOverColumn?.column === 'left'}
                        onDragOver={(e) => {
                            e.preventDefault();
                            e.stopPropagation();

                            if (!isPointerInsideCurrentTarget(e)) {
                                setIsOverColumn(null);
                                return;
                            }

                            setIsOverColumn({ sectionId: section.id, column: 'left' });
                        }}
                        onDragLeave={(e) => {
                            e.stopPropagation();
                            setIsOverColumn(null);
                        }}
                        onDrop={(e) => onColumnDrop(e, section.id, 'left')}
                    >
                        {section.columns?.left.map((element, index) => (
                            <React.Fragment key={element.id}>
                                {renderElementInsertZone('left', index)}
                                <InnerElement
                                    element={element}
                                    selectedElement={selectedElement}
                                    setSelectedElement={setSelectedElement}
                                    onDelete={() => onDeleteFromColumn(section.id, 'left', element.id)}
                                    onDuplicate={() => onDuplicateFromColumn(section.id, 'left', element.id)}
                                    onOpenSettings={(config) => onOpenSettings({
                                        ...config,
                                        sectionId: section.id,
                                        column: 'left'
                                    })}
                                    onHoverChange={setIsElementHovered}
                                    pendingChanges={pendingChanges}
                                    sectionId={section.id}
                                    column="left"
                                    primaryFont={primaryFont}
                                    onDragStart={() => onElementDragStart(section.id, 'left', element.id)}
                                    onDragEnd={onElementDragEnd}
                                />
                            </React.Fragment>
                        ))}

                        {renderElementInsertZone('left', section.columns?.left.length || 0)}

                        {!hasLeftContent && !isDraggingElement && (
                            <ColumnPlaceholder
                                theme={theme}
                                isOver={isOverColumn?.sectionId === section.id && isOverColumn?.column === 'left'}
                                style={{ height: '62px', padding: 0 }}
                            />
                        )}
                    </ColumnContainer>

                    <ColumnContainer
                        theme={theme}
                        isOver={isOverColumn?.sectionId === section.id && isOverColumn?.column === 'right'}
                        onDragOver={(e) => {
                            e.preventDefault();
                            e.stopPropagation();

                            if (!isPointerInsideCurrentTarget(e)) {
                                setIsOverColumn(null);
                                return;
                            }

                            setIsOverColumn({ sectionId: section.id, column: 'right' });
                        }}
                        onDragLeave={(e) => {
                            e.stopPropagation();
                            setIsOverColumn(null);
                        }}
                        onDrop={(e) => onColumnDrop(e, section.id, 'right')}
                    >
                        {section.columns?.right.map((element, index) => (
                            <React.Fragment key={element.id}>
                                {renderElementInsertZone('right', index)}
                                <InnerElement
                                    element={element}
                                    selectedElement={selectedElement}
                                    setSelectedElement={setSelectedElement}
                                    onDelete={() => onDeleteFromColumn(section.id, 'right', element.id)}
                                    onDuplicate={() => onDuplicateFromColumn(section.id, 'right', element.id)}
                                    onOpenSettings={(config) => onOpenSettings({
                                        ...config,
                                        sectionId: section.id,
                                        column: 'right'
                                    })}
                                    onHoverChange={setIsElementHovered}
                                    pendingChanges={pendingChanges}
                                    sectionId={section.id}
                                    column="right"
                                    primaryFont={primaryFont}
                                    onDragStart={() => onElementDragStart(section.id, 'right', element.id)}
                                    onDragEnd={onElementDragEnd}
                                />
                            </React.Fragment>
                        ))}

                        {renderElementInsertZone('right', section.columns?.right.length || 0)}

                        {!hasRightContent && !isDraggingElement && (
                            <ColumnPlaceholder
                                theme={theme}
                                isOver={isOverColumn?.sectionId === section.id && isOverColumn?.column === 'right'}
                                style={{ height: '62px', padding: 0 }}
                            />
                        )}
                    </ColumnContainer>
                </MultiColumnGrid>
            )}
        </SectionContainerWrapper>
    );
};

export default SectionContainer;